import { Injectable } from '@angular/core';
import { AppService } from '@app/app.service';
import { ApiService } from '@core/services/api.service';
import { environment } from '@environments/environment';
import { Subject } from 'rxjs';
import { delay, retryWhen, take, tap } from 'rxjs/operators';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';

@Injectable({
    providedIn: 'root',
})
export class AlertNotificationService {
    private socket: WebSocketSubject<any> = null;
    private notificationPermission: NotificationPermission = 'default';
    private connected = false;
    private connectCalled = false;
    private connectedHosts = new Set<string>();
    private socketSubscriptions: any[] = [];

    // Observable for components to subscribe to new alerts
    public alertReceived = new Subject<any>();

    constructor(
        private appSrvc: AppService,
        private apiService: ApiService
    ) {
        this.requestNotificationPermission();
    }

    requestNotificationPermission(): void {
        if ('Notification' in window && Notification.permission === 'default') {
            Notification.requestPermission().then((permission) => {
                this.notificationPermission = permission;
            });
        } else if ('Notification' in window) {
            this.notificationPermission = Notification.permission;
        }
    }

    public connect(): void {
        if (this.connectCalled) return;
        this.connectCalled = true;

        // Fetch hosts and connect WebSocket
        this.apiService.apiRequest('GET', '/hosts', {}, false, { active: true })
            .subscribe(
                (res) => {
                    if (res?.data?.length) {
                        res.data.forEach((host) => {
                            this.connectToHost(host.host_url, host.host_name);
                        });
                    }
                },
                () => {} // Silently ignore - prevents 401 logout cascade
            );
    }

    private connectToHost(hostUrl: string, hostName?: string): void {
        let url = hostUrl.includes('https://')
            ? hostUrl.split('https://')[1]
            : hostUrl.split('http://')[1];
        if (url.includes('/')) {
            url = url.slice(0, -1);
        }
        const wsUrl = hostUrl.includes('https://')
            ? `wss://${url}:8000`
            : `ws://${url}:8000`;

        // Prevent duplicate connections to the same host
        if (this.connectedHosts.has(wsUrl)) return;
        this.connectedHosts.add(wsUrl);

        const socket = webSocket({
            url: wsUrl,
            openObserver: {
                next: () => {
                    this.connected = true;
                    // Subscribe to TSOD/alert messages
                    socket.next({ type: 'starttsod' });
                },
            },
            closeObserver: {
                next: () => {
                    this.connectedHosts.delete(wsUrl);
                    this.connected = this.connectedHosts.size > 0;
                },
            },
        });

        const sub = socket
            .pipe(
                tap({
                    error: () => {
                        console.log(`[AlertNotification] WebSocket error with ${hostName || hostUrl}`);
                    },
                }),
                retryWhen((errors) =>
                    errors.pipe(
                        tap(() => console.log('[AlertNotification] Reconnecting...')),
                        take(3),
                        delay(5000)
                    )
                )
            )
            .subscribe((message: any) => {
                if (message.type === 'channels') {
                    socket.next({ type: 'starttsod' });
                }
                if (message.type === 'blackframe' && localStorage.getItem('web_push_enabled') !== '0') {
                    this.handleAlertMessage(message, hostName);
                }
            });
        this.socketSubscriptions.push(sub);
    }

    private alertQueue: { text: string; host: string }[] = [];
    private isShowingSnackbar = false;
    private lastNotifiedTime = 0;
    private readonly THROTTLE_MS = 60000;

    private handleAlertMessage(message: any, hostName?: string): void {
        this.alertReceived.next(message);

        // Global throttle: ignore all messages for 6 seconds after a notification
        const now = Date.now();
        if (now - this.lastNotifiedTime < this.THROTTLE_MS) {
            return;
        }
        this.lastNotifiedTime = now;
        const parts = (message.data || 'Signal Loss Detected').split(' ');
        let alertType = parts[0] || 'Signal Loss Detected';
        if(alertType === 'PID_RESUMED') {
            alertType = 'Signal Resumed';
        } else if(alertType === 'PID_MISSING') {
            alertType = 'Signal Lost';
        }
        const sender = parts[parts.length - 1] || ''; // e.g. miqlab4
        const alertText = `${alertType} - ${sender}`;
        const host = hostName || '';

        this.alertQueue.push({ text: alertText, host });
        this.showNextSnackbar();
        if (localStorage.getItem('web_push_enabled') !== '0') {
            this.showBrowserNotification(alertText, host);
        }
        this.speakAlert(alertType, sender);
    }

    private showNextSnackbar(): void {
        if (this.isShowingSnackbar || this.alertQueue.length === 0) return;

        this.isShowingSnackbar = true;
        const alert = this.alertQueue.shift();
        const msg = alert.host ? `${alert.text} - ${alert.host}` : alert.text;

        this.appSrvc.openSnackBar(msg, 'OK', 6000, 'right', 'bottom');
        setTimeout(() => {
            this.isShowingSnackbar = false;
            this.showNextSnackbar();
        }, 6000);
    }

    private showBrowserNotification(alertText: string, host: string): void {
        if (!document.hasFocus() && this.notificationPermission === 'granted') {
            const notification = new Notification('MonitorIQ Alert', {
                body: host ? `${alertText}\n${host}` : alertText,
                icon: 'assets/images/favicon.png',
                tag: `alert-${Date.now()}`,
            });

            notification.onclick = () => {
                window.focus();
                notification.close();
            };

            setTimeout(() => notification.close(), 6000);
        }
    }

    private speakAlert(alertType: string, sender: string): void {
        if (localStorage.getItem('tts_alerts_enabled') !== '1') return;
        if (!('speechSynthesis' in window)) return;

        // Cancel any ongoing speech before speaking new alert
        window.speechSynthesis.cancel();

        const spellOut = (text: string) => text.split('').join(' ');
        const spokenAlertType = alertType.includes('_') ? alertType.split('_').join(' ') : alertType;
        const msg = sender
            ? `${spokenAlertType}, ${spellOut(sender)}`
            : spokenAlertType;
        const utterance = new SpeechSynthesisUtterance(msg);
        const preferredVoice = localStorage.getItem('tts_alert_voice');
        const preferredLang = localStorage.getItem('tts_alert_lang');
        if (preferredVoice || preferredLang) {
            const voices = window.speechSynthesis.getVoices();
            // Prefer the exact voice when present on this device, otherwise
            // fall back to any voice matching the chosen locale.
            const voice =
                (preferredVoice &&
                    voices.find((v) => v.name === preferredVoice)) ||
                (preferredLang &&
                    voices.find((v) => v.lang === preferredLang)) ||
                null;
            if (voice) {
                utterance.voice = voice;
                utterance.lang = voice.lang;
            }
        }
        utterance.rate = 0.9;
        utterance.pitch = 1;
        utterance.volume = 1;
        window.speechSynthesis.speak(utterance);
    }

    public disconnect(): void {
        this.socketSubscriptions.forEach(sub => sub?.unsubscribe());
        this.socketSubscriptions = [];
        this.connectedHosts.clear();
        this.connected = false;
        this.connectCalled = false;
    }

    public isConnected(): boolean {
        return this.connectCalled;
    }
}
